import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Star, CheckCircle } from "@phosphor-icons/react";
import CelsiusLogo from "@/components/CelsiusLogo";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Public (no-auth) axios instance
const pub = axios.create({ baseURL: API });

function StarPicker({ value, onChange }) {
  return (
    <div className="flex gap-2" data-testid="star-picker">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          onClick={() => onChange(n)}
          data-testid={`star-${n}`}
          className={`p-2 rounded-sm transition-colors ${value >= n ? "text-amber-500" : "text-slate-300 hover:text-amber-300"}`}
        >
          <Star size={36} weight={value >= n ? "fill" : "regular"} />
        </button>
      ))}
    </div>
  );
}

function NPSPicker({ value, onChange }) {
  const npsClass = (score, active) => {
    if (!active) return "bg-white border-slate-300 text-slate-700 hover:border-[#EA580C]";
    if (score >= 9) return "bg-emerald-600 text-white border-emerald-600";
    if (score >= 7) return "bg-amber-500 text-white border-amber-500";
    return "bg-red-600 text-white border-red-600";
  };
  return (
    <div className="grid grid-cols-11 gap-1" data-testid="nps-picker">
      {Array.from({ length: 11 }).map((_, i) => (
        <button
          key={`nps-${i}`}
          onClick={() => onChange(i)}
          data-testid={`nps-${i}`}
          className={`py-2 text-sm font-mono-num font-bold border rounded-sm transition-colors ${npsClass(i, value === i)}`}
        >
          {i}
        </button>
      ))}
    </div>
  );
}

export default function PublicFeedback() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [rating, setRating] = useState(0);
  const [nps, setNps] = useState(-1);
  const [comment, setComment] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    pub.get(`/public/feedback/${token}`)
      .then((r) => {
        setState({ loading: false, data: r.data, error: "" });
        if (r.data.already_submitted) {
          setSubmitted(true);
          setRating(r.data.existing_rating || 0);
          setNps(r.data.existing_nps ?? -1);
          setComment(r.data.existing_comment || "");
        }
      })
      .catch((e) => setState({ loading: false, data: null, error: e.response?.data?.detail || "Invalid or expired link" }));
  }, [token]);

  const submit = async () => {
    if (rating < 1) return toast.error("Please rate your experience (1–5 stars)");
    if (nps < 0) return toast.error("Please pick a 0–10 recommendation score");
    setSubmitting(true);
    try {
      await pub.post(`/public/feedback/${token}`, { rating, nps, comment });
      setSubmitted(true);
      toast.success("Thank you for your feedback!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (state.loading) return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F0F2F5] p-6">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-sm p-8 text-center">
          <div className="text-red-600 font-display text-2xl font-bold mb-2">Link not valid</div>
          <div className="text-slate-500 text-sm">{state.error}</div>
        </div>
      </div>
    );
  }

  const t = state.data;

  return (
    <div className="min-h-screen bg-[#F0F2F5] flex items-center justify-center p-4 md:p-8" data-testid="public-feedback-page">
      <div className="max-w-2xl w-full bg-white border border-slate-200 rounded-sm">
        {/* Header */}
        <div className="bg-[#0A0E1A] text-white p-6 flex items-center justify-between">
          <CelsiusLogo variant="full" scheme="dark" size={32} />
          <div className="text-[10px] uppercase tracking-widest text-slate-400">Service Feedback</div>
        </div>

        {/* Ticket info */}
        <div className="p-6 border-b border-slate-100 bg-slate-50">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Ticket</div>
          <div className="font-mono-num text-[#EA580C] font-bold text-lg mt-1">{t.ticket_no}</div>
          <div className="text-slate-800 text-sm mt-2">{t.complaint}</div>
          {t.resolution_notes && (
            <>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mt-4">Resolution</div>
              <div className="text-slate-700 text-sm mt-1">{t.resolution_notes}</div>
            </>
          )}
        </div>

        {/* Feedback form */}
        <div className="p-6 md:p-8 space-y-6">
          {submitted ? (
            <div className="text-center py-6" data-testid="feedback-submitted">
              <CheckCircle size={48} className="text-emerald-500 mx-auto" weight="fill" />
              <div className="font-display text-2xl font-bold mt-3">Thank you!</div>
              <div className="text-slate-500 text-sm mt-2">
                We recorded {rating} ★ and NPS {nps}. Your feedback helps us improve.
              </div>
              {comment && <div className="mt-4 italic text-slate-600 text-sm">&ldquo;{comment}&rdquo;</div>}
            </div>
          ) : (
            <>
              <div>
                <div className="text-[13px] font-semibold text-slate-800 mb-2">How would you rate the service?</div>
                <StarPicker value={rating} onChange={setRating} />
              </div>
              <div>
                <div className="text-[13px] font-semibold text-slate-800 mb-2">
                  How likely are you to recommend us to a friend or colleague? <span className="text-slate-500 font-normal">(0 = not likely, 10 = extremely likely)</span>
                </div>
                <NPSPicker value={nps} onChange={setNps} />
              </div>
              <div>
                <div className="text-[13px] font-semibold text-slate-800 mb-2">Comments (optional)</div>
                <Textarea
                  rows={4}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Anything else you'd like to share?"
                  data-testid="feedback-comment-input"
                />
              </div>
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="submit-feedback-button"
                className="w-full rounded-sm bg-[#EA580C] hover:bg-[#c2410c] h-11"
              >
                {submitting ? "Submitting…" : "Submit Feedback"}
              </Button>
            </>
          )}
        </div>

        <div className="text-center text-[10px] text-slate-400 uppercase tracking-widest py-3 border-t border-slate-100">
          © Celsius · Powered by SAP Fiori · SV Module
        </div>
      </div>
    </div>
  );
}
